/**
 * Vuetify 3 setup: VS Code Dark+ theme + MDI icons.
 *
 * The palette mirrors VS Code's default dark theme so the app reads like an
 * extension of the editor. Both the surface greys and the `on-*` foreground
 * variants are declared explicitly: Vuetify derives `on-surface`/`on-background`
 * automatically from `surface`/`background`, but pinning the foreground values
 * avoids the black-on-dark text that appears in menu/list items when a card
 * inherits a plain background without an `on-*` override.
 *
 * Component/directive/style auto-import is handled by vite-plugin-vuetify.
 */
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import { createVuetify, type ThemeDefinition } from 'vuetify'

const dark: ThemeDefinition = {
  dark: true,
  colors: {
    // Editor / chrome backgrounds (VS Code Dark+).
    background: '#1e1e1e', // editor background
    surface: '#252526', // side bar / panels / toolbars
    'surface-variant': '#37373d', // raised elements (incoming bubbles, inputs, hover)
    'on-surface': '#cccccc', // foreground on surface
    'on-background': '#d4d4d4', // foreground on background
    'on-surface-variant': '#cccccc',
    // Accents.
    primary: '#0e639c', // VS Code blue (title bar / selection bar) — deeper than #007acc for contrast on dark
    'on-primary': '#ffffff',
    secondary: '#3a3d41', // hover / inactive
    'on-secondary': '#ffffff',
    error: '#f14c4c', // VS Code error red (brighter, readable on dark)
    'on-error': '#ffffff',
    info: '#4fc1ff',
    'on-info': '#000000',
    success: '#4ec9b0', // VS Code teal-green (status)
    'on-success': '#000000',
    warning: '#cca700',
    'on-warning': '#000000',
  },
  variables: {
    // Vuetify derives many greys from these; raising them keeps borders/dividers
    // and hover states visible on the dark surface instead of fading to black.
    'border-color': '#3c3c3c',
    'border-opacity': 1,
    'high-emphasis-opacity': 1,
    'medium-emphasis-opacity': 0.78,
    'disabled-opacity': 0.38,
  },
}

const vuetify = createVuetify({
  theme: {
    defaultTheme: 'dark',
    themes: { dark },
  },
  icons: {
    defaultSet: 'mdi',
  },
})

export default vuetify
